import { useCallback, useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { TaskListQuery } from "@common/contracts/task.contract";
import {
    fetchOrganizationTasks,
    fetchOrganizationTaskThreadUnreadCounts,
    organizationTaskQueryKeys,
} from "@api/organizationTask.api";
import { useWorkspacesList } from "@api/workspace.api";
import { useScope } from "@state/scopeContext.lib";
import { compareTasks, isClientSort } from "@components/tasks/compareTasks";
import { withUnreadCount } from "@components/tasks/withUnreadCount";
import { useBusyClosesDemoTasks } from "@components/demo/busyClosesDemo/useBusyClosesDemo";
import { useKeptRows } from "@hooks/useKeptRows";

const toServerQuery = (clientSortedQuery: TaskListQuery): TaskListQuery =>
    isClientSort(clientSortedQuery.sort)
        ? { ...clientSortedQuery, sort: undefined }
        : clientSortedQuery;

/**
 * Every client's tasks for the query, sorted on the client. `tasks` is `null`
 * until the organization's tasks answered; until then a filter, search or sort
 * change keeps the last tasks, marked `isPlaceholderData`. Unread counts load
 * beside the tasks and never hold them back.
 */
export function useOrganizationTasks(clientSortedQuery: TaskListQuery) {
    const query = useMemo(
        () => toServerQuery(clientSortedQuery),
        [clientSortedQuery],
    );
    const { organizationId } = useScope();
    const { data: workspaces, isLoading: areWorkspacesLoading } =
        useWorkspacesList(organizationId ? { organizationId } : undefined, {
            enabled: !!organizationId,
        });

    const {
        data: fetchedOrganizationTasks,
        isLoading,
        isError,
        refetch: refetchTasks,
    } = useQuery({
        queryKey: organizationTaskQueryKeys.tasks(organizationId ?? "", query),
        queryFn: () =>
            fetchOrganizationTasks({
                organizationId: organizationId ?? "",
                query,
            }),
        enabled: !!organizationId,
    });

    const { data: unreadCounts } = useQuery({
        queryKey: organizationTaskQueryKeys.threadUnreadCounts(
            organizationId ?? "",
            query,
        ),
        queryFn: () =>
            fetchOrganizationTaskThreadUnreadCounts({
                organizationId: organizationId ?? "",
                query,
            }),
        enabled: !!organizationId,
        placeholderData: keepPreviousData,
    });

    const clientNamesById = useMemo(
        () =>
            new Map(
                (workspaces ?? []).map((workspace) => [
                    workspace.id,
                    workspace.name,
                ]),
            ),
        [workspaces],
    );

    const clientIconColorsById = useMemo(
        () =>
            new Map(
                (workspaces ?? []).map((workspace) => [
                    workspace.id,
                    workspace.iconColor,
                ]),
            ),
        [workspaces],
    );

    const shownTasks = useBusyClosesDemoTasks({
        tasks: fetchedOrganizationTasks ?? null,
        scope: "organization",
        query: clientSortedQuery,
    });

    const tasks = useMemo(() => {
        const unreadCountsByTaskId = new Map(
            (unreadCounts ?? []).map(({ taskId, unreadCount }) => [
                taskId,
                unreadCount,
            ]),
        );
        return (shownTasks ?? [])
            .toSorted(
                compareTasks({
                    sort: clientSortedQuery.sort,
                    groupBy: clientSortedQuery.groupBy,
                    clientNamesById,
                }),
            )
            .map((task) =>
                withUnreadCount(task, unreadCountsByTaskId.get(task.id) ?? 0),
            );
    }, [
        shownTasks,
        unreadCounts,
        clientSortedQuery.sort,
        clientSortedQuery.groupBy,
        clientNamesById,
    ]);

    const { rows: keptTasks, isRefetching } = useKeptRows({
        rows: areWorkspacesLoading || isLoading ? null : tasks,
        datasetKey: organizationId ?? "",
    });

    const retryFailed = useCallback(() => {
        void refetchTasks();
    }, [refetchTasks]);

    return {
        tasks: keptTasks,
        clientNamesById,
        clientIconColorsById,
        isPlaceholderData: isRefetching,
        hasLoadError: isError,
        retryFailed,
    };
}
